import { Component, Vue } from 'vue-property-decorator';
import StripePurchaseIntent from '~/queries/stripePurchaseIntent.gql';
import { IDocumentsAccepted } from '../types/legal-documents';
import { getParsedCookie } from '../utils';
import {
  DefaultValuesOption,
  PaymentIntent,
  StripeElements,
} from '@stripe/stripe-js';
import { getStripeClient } from '../utils/getStripeClient';

@Component
export default class Stripe extends Vue {
  async stripeMixinConfirmStripePayment(
    stripeElement: StripeElements,
    payload: {
      productId: string;
      quantity: number;
      expectedPrice: number;
      totpToken: string;
      acceptedDocuments: IDocumentsAccepted;
      priceAdjustmentCode?: string;
      productContext?: { nameToken?: string };
    },
    returnUrl: string = `${window.location.origin}/order`,
    redirectOnSuccess: boolean = true,
  ): Promise<{
    success: boolean;
    message?: string;
    paymentIntent?: PaymentIntent;
  }> {
    const client = await getStripeClient();

    if (!stripeElement) {
      throw new Error('stripeElement is missing');
    }

    const submitResponse = await this.stripeMixinSubmitForm(stripeElement);
    if (submitResponse.error) {
      return {
        success: false,
        message: submitResponse.error.message,
      };
    }

    try {
      const { error, confirmationToken } = await client.createConfirmationToken(
        {
          elements: stripeElement,
        },
      );

      if (error) {
        this.$sentry.captureException(error);
        return {
          success: false,
          message: error.message,
        };
      }

      if (!confirmationToken) {
        throw new Error('Stripe createConfirmationToken did not succeed');
      }

      const purchaseIntentPayload = {
        ...payload,
        confirmationTokenId: confirmationToken.id,
        returnUrl,
        acceptedDocuments: payload.acceptedDocuments.map(
          ({ documentType, versionAccepted }) => ({
            documentType,
            versionAccepted,
          }),
        ),
        orderContext: getParsedCookie('utmVariables') || undefined,
      };

      const purchaseIntentResponse = await this.$apollo.query({
        query: StripePurchaseIntent,
        variables: purchaseIntentPayload,
        fetchPolicy: 'network-only',
      });

      const {
        clientSecret,
        paymentIntent,
        orderId,
      } = purchaseIntentResponse.data.stripePurchaseIntent;
      if (!clientSecret) {
        throw new Error('Stripe client secret is missing from server response');
      }

      const handleSuccess = () => {
        if (redirectOnSuccess) {
          const finalUrl = `${returnUrl}?payment_intent=${paymentIntent.id}&payment_intent_client_secret=${clientSecret}&orderId=${orderId}&redirect_status=succeeded`;
          window.location.href = finalUrl;
        }

        return {
          success: true,
        };
      };

      if (paymentIntent.status === 'succeeded') {
        return handleSuccess();
      }

      if (paymentIntent.status === 'requires_action') {
        const interim = await client.handleNextAction({ clientSecret });
        if (interim.error) {
          return {
            success: false,
            message: interim.error.message,
          };
        }

        if (interim.paymentIntent?.status === 'succeeded') {
          return handleSuccess();
        }
      }

      return {
        success: false,
        message: 'There was an issue with your payment.',
      };
    } catch (e) {
      this.$sentry.captureException(e);
      return {
        success: false,
        message: 'There was an error processing the request, please try again',
      };
    }
  }

  async stripeMixinSubmitForm(stripeElement: StripeElements) {
    return stripeElement.submit();
  }

  async stripeMixinCreateStripeElement(
    containerElement: string,
    amount: number,
    defaultValues: DefaultValuesOption = {},
  ) {
    const client = await getStripeClient();

    const stripeElement = client.elements({
      appearance: {
        theme: 'night',
      },
      mode: 'payment',
      amount,
      currency: 'usd',
    });

    const card = stripeElement.create('payment', { defaultValues });
    card.mount(containerElement);

    return stripeElement;
  }

  stripeMixinDestroyStripeElement(stripeElement: StripeElements) {
    if (stripeElement) {
      stripeElement.getElement('payment')?.destroy();
    }
  }
}
